/**
 * ScopedWatchlistPickerContainer - Logic for the scoped watchlist picker
 *
 * Fetches the user's watchlists and delegates rendering to the View.
 * Used on desktop to show the watchlist picker within the player detail panel
 * via GorhomPortal as a bottom sheet instead of a full-viewport Actionsheet.
 * @remarks This is a Container component - handles business logic and state
 */

import { useMemo } from "react";

import { CustomPromptRefProps } from "@/components/molecules/CustomPrompt/CustomPromptContainer";
import {
  PlayerSearchPlayerFragment,
  useWatchlistListMyUserWatchlistsQuery,
} from "@/generated/graphql";
import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";

import ScopedWatchlistPickerView from "./ScopedWatchlistPickerView";

/**
 * Props for ScopedWatchlistPickerContainer
 */
interface ScopedWatchlistPickerContainerProps {
  /** Player data to add to watchlists */
  readonly player: PlayerSearchPlayerFragment;
  /** Whether the picker is open */
  readonly isOpen: boolean;
  /** Callback when the picker should close */
  readonly onClose: () => void;
  /** Callback to create a new watchlist */
  readonly onAddWatchlistPress: () => void;
  /** Whether a watchlist operation is in progress */
  readonly loading: boolean;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Ref for the custom prompt dialog used by "+ New List" */
  readonly promptRef: React.RefObject<CustomPromptRefProps | null>;
}

/**
 * ScopedWatchlistPickerContainer fetches watchlists and renders the scoped
 * bottom-sheet watchlist picker for desktop
 * @param props - Container props
 * @param props.player - Player data
 * @param props.isOpen - Whether the picker is open
 * @param props.onClose - Close handler
 * @param props.onAddWatchlistPress - New list handler
 * @param props.loading - Whether loading
 * @param props.isDark - Whether dark mode is active
 * @param props.promptRef - Ref for the custom prompt dialog used by "+ New List"
 * @returns ScopedWatchlistPickerView with fetched watchlists
 */
const ScopedWatchlistPickerContainer = ({
  player,
  isOpen,
  onClose,
  onAddWatchlistPress,
  loading,
  isDark,
  promptRef,
}: ScopedWatchlistPickerContainerProps) => {
  const { data: watchlistsData } = useWatchlistListMyUserWatchlistsQuery({
    variables: { first: MY_WATCHLISTS_PAGE_SIZE },
    skip: !isOpen,
  });

  const watchlists = useMemo(
    () =>
      watchlistsData?.listMyUserWatchlists.edges.map(
        edge => edge.node.watchlist
      ) ?? [],
    [watchlistsData]
  );

  return (
    <ScopedWatchlistPickerView
      player={player}
      isOpen={isOpen}
      onClose={onClose}
      watchlists={watchlists}
      onAddWatchlistPress={onAddWatchlistPress}
      loading={loading}
      isDark={isDark}
      promptRef={promptRef}
    />
  );
};

ScopedWatchlistPickerContainer.displayName = "ScopedWatchlistPickerContainer";

export default ScopedWatchlistPickerContainer;
